import { gql, useQuery } from '@chronosphereio/core';
import { useMemo } from 'react';
import { GetUserRoles, GetUserRolesContext, GetUserRolesVariables } from '@/generated/graphql';

export enum Role {
  EDITOR = 'Editor',
  NONE = 'None',
  SYSTEM_ADMIN = 'SysAdmin',
  USER_ADMIN = 'UserAdmin',
}

export type RoleName = { name: string };

/**
 * Helper function that returns a list of valid roles for the API.
 */
export const sanitizeRoles = (roles: string[], defaultRole: string) => {
  return roles.filter((r) => r !== defaultRole);
};

export function getDefaultRole() {
  return Role.NONE;
}

const ROLE_RANKING = {
  [Role.SYSTEM_ADMIN]: 1,
  [Role.USER_ADMIN]: 2,
  [Role.EDITOR]: 3,
  [Role.NONE]: 4,
};

// Get the role of the entity by getting the highest ranked role from the list of roles
export const getEntityRole = (roles: RoleName[], defaultRole: string) => {
  return roles.sort((a, b) => ROLE_RANKING[a.name as Role] - ROLE_RANKING[b.name as Role]).at(0)?.name ?? defaultRole;
};

const GET_USER_ROLES = gql`
  query GetUserRoles {
    me {
      roles {
        name
      }
    }
  }
`;

export const useGetUserRoles = ({ pause }: { pause: boolean }) => {
  const [{ data, fetching, error }] = useQuery<GetUserRoles, GetUserRolesVariables>({
    query: GET_USER_ROLES,
    context: GetUserRolesContext,
    pause,
  });

  const userRoles = useMemo(() => {
    return data?.me.roles ?? [];
  }, [data?.me.roles]);

  const defaultRole = getDefaultRole();
  const userRole = getEntityRole(userRoles, defaultRole);

  return { userRole, fetching, error };
};
